/**
 * ⚖ A conversation's HEAD opens its ASSEMBLED page.
 *
 * `index-builder` writes an enriched, navigable rendering of a WhatsApp
 * conversation — the message stream with its media table, inline descriptions
 * and the full-transcript link — as a SIDECAR of the conversation source:
 * `sources/whatsapp/<stem>.conversation.md` (`mission-ingest`, 2026-08-11;
 * previously written under `analyses/`).
 *
 * The head row itself is the raw `.txt` export. Left to extension dispatch it
 * opens in `TextViewer` — a wall of unformatted export text, which is the worst
 * of the three representations we hold of the same conversation. So the head
 * opens the page instead.
 *
 * ## Why the head is identified by `node_id` and not by its filename
 *
 * The tempting rule is *"path ends `conversa-mensagens*.txt` → swap the
 * extension"*. That is a closed map over a producer's naming convention, and it
 * is the shape that has already cost this codebase twice (hardcoded category
 * ids; a label that moved). `index-builder` renames things; a pattern would rot
 * silently and the head would quietly go back to opening raw text — with
 * nothing failing.
 *
 * A compound head is *exactly* a catalog row carrying `node_id`
 * (`compound-upload-grouping-miniplan.md`), which the Acervo already resolves
 * per tree path to build its nesting. Keying on that means the rule that makes
 * a row a head and the rule that decides what it opens share ONE fact and
 * cannot disagree.
 *
 * ## Why existence is never inferred
 *
 * This module only derives a PATH. Whether the sidecar is there is answered by
 * attempting the read, never by looking in the tree: the runtime keeps
 * companions out of the lawyer's tree, so a tree lookup would report "absent"
 * for every conversation ever written. See `ConversationPageViewer`.
 */

/** The suffix `index-builder` writes the assembled page under. */
export const CONVERSATION_PAGE_SUFFIX = ".conversation.md";

/**
 * The assembled-page path for a conversation head, or null when the input is
 * not a plausible source path.
 *
 * Replaces the FINAL extension only: `a/b/conversa.txt` → `a/b/conversa.conversation.md`.
 * A path with no extension gets the suffix appended rather than being rejected —
 * losing the page would be worse than asking for one that may not exist, and
 * the read attempt settles it either way.
 */
export function conversationPagePathFor(path: string): string | null {
  const trimmed = path.trim();
  if (!trimmed) return null;
  // Already the page itself — never derive a `.conversation.conversation.md`.
  if (trimmed.endsWith(CONVERSATION_PAGE_SUFFIX)) return trimmed;

  const slash = trimmed.lastIndexOf("/");
  const name = slash >= 0 ? trimmed.slice(slash + 1) : trimmed;
  const dot = name.lastIndexOf(".");
  // A leading dot is a dotfile, not an extension.
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const dir = slash >= 0 ? trimmed.slice(0, slash + 1) : "";
  return `${dir}${stem}${CONVERSATION_PAGE_SUFFIX}`;
}
